import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { createRouter, createWebHistory } from 'vue-router'
import App from './App.vue'
import './style.css'
import config from './config.js'
import { useAuthStore } from './stores/authStore.js'
import apiClient from './utils/api.js'

// Import views
import LeadCapture from './views/LeadCapture.vue'
import AdminLogin from './views/AdminLogin.vue'
import AdminDashboard from './views/AdminDashboard.vue'
import SuperAdminDashboard from './views/SuperAdminDashboard.vue'
import AdminLeads from './views/AdminLeads.vue'
import AdminShows from './views/AdminShows.vue'
import AdminSettings from './views/AdminSettings.vue'
import SuperAdminClients from './views/SuperAdminClients.vue'
import SuperAdminQuotes from './views/SuperAdminQuotes.vue'

// Create router
const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/booth/:clientSlug/:showSlug',
      name: 'LeadCapture',
      component: LeadCapture,
      props: true
    },
    {
      path: '/admin/login',
      name: 'AdminLogin',
      component: AdminLogin
    },
    {
      path: '/admin/dashboard',
      name: 'AdminDashboard',
      component: AdminDashboard,
      meta: { requiresAuth: true }
    },
    {
      path: '/admin/super/dashboard',
      name: 'SuperAdminDashboard',
      component: SuperAdminDashboard,
      meta: { requiresAuth: true, requiresSuperAdmin: true }
    },
    {
      path: '/admin/leads',
      name: 'AdminLeads',
      component: AdminLeads,
      meta: { requiresAuth: true }
    },
    {
      path: '/admin/shows',
      name: 'AdminShows',
      component: AdminShows,
      meta: { requiresAuth: true }
    },
    {
      path: '/admin/settings',
      name: 'AdminSettings',
      component: AdminSettings,
      meta: { requiresAuth: true }
    },
    {
      path: '/admin/super/clients',
      name: 'SuperAdminClients',
      component: SuperAdminClients,
      meta: { requiresAuth: true, requiresSuperAdmin: true }
    },
    {
      path: '/admin/super/quotes',
      name: 'SuperAdminQuotes',
      component: SuperAdminQuotes,
      meta: { requiresAuth: true, requiresSuperAdmin: true }
    },
    {
      path: '/',
      redirect: '/admin/login'
    }
  ]
})

// Navigation guard for admin routes
router.beforeEach(async (to, from, next) => {
  if (to.meta.requiresAuth) {
    // Use auth store for consistent authentication
    const authStore = useAuthStore()
    
    // Check if we have a valid token
    if (!authStore.hasValidToken) {
      next('/admin/login')
      return
    }
    
    // Validate token with backend (single validation per navigation)
    const isValid = await authStore.checkAuthStatus()
    if (!isValid) {
      next('/admin/login')
      return
    }
    
    // Check super admin access for super admin routes
    if (to.meta.requiresSuperAdmin) {
      if (authStore.client?.role !== 'super_admin') {
        next('/admin/dashboard')
        return
      }
    }
    
    // Role-based dashboard routing
    if (to.path === '/admin/dashboard') {
      if (authStore.client?.role === 'super_admin') {
        next('/admin/super/dashboard')
        return
      }
    }
  }
  next()
})

// Create app
const app = createApp(App)
const pinia = createPinia()

app.use(pinia)
app.use(router)

// Initialize auth store and API client
const authStore = useAuthStore()
apiClient.setAuthStore(authStore)

// Initialize auth store with router
authStore.initialize(router)

app.mount('#app') 